/**
 * INVENTARIO (FASE 9.1) — maestros, artículos y almacenes
 *
 *   /inventario/articulos[/:id]     listado + ficha (Información · Códigos y ERP · Unidades · Almacenes · Historial)
 *   /inventario/almacenes[/:id]     listado + ficha (Información · Artículos · Historial)
 *   /inventario/maestros            unidades, tipos, familias, subfamilias
 *
 * Dominio TRANSVERSAL (Limpieza y Mantenimiento). La configuración
 * (mínimo, máximo, punto de pedido) no es un saldo: el stock (FASE 9.2) se
 * ve en la pestaña Stock de cada ficha y nunca se edita aquí. La clasificación se elige encadenada (tipo → familia →
 * subfamilia) pero la garantía está en la base (FK compuestas). La UI sólo
 * decide qué botones ofrecer; la API y la RLS de 0062 son la barrera.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  api,
  FalloApi,
  type AlmacenDetalle,
  type AlmacenResumen,
  type ArticuloDetalle,
  type ArticuloResumen,
  type ConfigArticuloAlmacen,
  type EventoInventario,
  type FamiliaArticulo,
  type IdentidadArticulo,
  type SistemaExterno,
  type TipoCodigoArticulo,
  type OpcionesInventario,
  type SubfamiliaArticulo,
  type TipoAlmacen,
  type TipoArticulo,
  type UnidadMedida,
} from '../api.ts';
import { DialogoEtiquetas } from '../componentes/etiquetas.tsx';
import { AsistenteRegularizacion } from './regularizacion.tsx';
import { BarraAcciones, ConfirmDialog, Error as ErrorCaja, Estado, fechaHora, FiltrosResponsive, Modal, Panel, useDatos, Vacio } from '../componentes/base.tsx';
import { CambiosEvento } from '../componentes/cambios-historial.tsx';
import { estadoArticulo } from '../estados.ts';
import { codigoAlmacen, CodigoArticulo, codigoVisible, DetallesTecnicos } from '../componentes/codigo-articulo.tsx';
import { PieHistorial, useHistorial } from '../componentes/historial.tsx';
import { Shell } from '../componentes/shell.tsx';
import { useSesion } from '../contexto.tsx';
import { Ledger, TablaStock } from './stock.tsx';
import { ListaLotes, ListaSeries } from './trazabilidad.tsx';

const TIPOS_ALMACEN: Readonly<Record<TipoAlmacen, string>> = { CENTRAL: 'Central', CENTRO: 'De centro', MOVIL: 'Móvil', OTRO: 'Otro' };

function erroresDe(e: unknown): Record<string, string> {
  return e instanceof FalloApi ? (e.erroresDeCampo ?? {}) : {};
}
function Dato({ e, v }: { e: string; v: ReactNode }): ReactNode {
  return (
    <div className="dato">
      <div className="etiqueta-dato">{e}</div>
      <div className="valor-dato">{v === null || v === '' ? '—' : v}</div>
    </div>
  );
}
const SiNo = ({ v, si = 'Sí', no = 'No' }: { v: boolean; si?: string; no?: string }): ReactNode => <span className={`etiqueta ${v ? 'info' : 'neutra'}`}>{v ? si : no}</span>;
const Activo = ({ v }: { v: boolean }): ReactNode => <Estado d={estadoArticulo(v)} />;
const Campo = ({ campos, k }: { campos: Record<string, string>; k: string }): ReactNode => (campos[k] === undefined ? null : <div className="error-campo">{campos[k]}</div>);
const cantidad = (v: string | null, unidad: string): string => (v === null ? '—' : `${v} ${unidad}`);
/** FASE 9.1-C — una referencia histórica a algo inactivo sigue resolviéndose, marcada. */
const conEstado = (texto: string, activo: boolean | null | undefined): string => (activo === false ? `${texto} (Inactivo)` : texto);

function Pestanas({ opciones, actual, fijar }: { opciones: readonly (readonly [string, string])[]; actual: string; fijar: (k: string) => void }): ReactNode {
  return (
    <nav className="tabs" role="tablist" aria-label="Secciones">
      {opciones.map(([k, t]) => (
        <a key={k} href={`?tab=${k}`} role="tab" aria-selected={actual === k} className={actual === k ? 'activo' : ''}
          onClick={(ev) => { ev.preventDefault(); fijar(k); }}>{t}</a>
      ))}
    </nav>
  );
}

function Historial({ eventos }: { eventos: readonly EventoInventario[] }): ReactNode {
  const TEXTO: Readonly<Record<string, string>> = {
    ALTA: 'Alta', EDICION: 'Edición', CLASIFICACION: 'Clasificación', UNIDAD: 'Unidad de stock', INVENTARIABLE: 'Inventariable',
    TRAZABILIDAD: 'Trazabilidad', ESTADO: 'Estado', CONVERSION: 'Conversión', ALMACEN: 'Configuración en almacén',
    TIPO_CENTRO: 'Tipo / centro', STOCK_NEGATIVO: 'Política de stock negativo',
    REFERENCIA_EXTERNA: 'Referencia ERP', CODIGO: 'Código de barras', CODIGO_NEGOCIO: 'Cambio de código',
  };
  //  F13.5-A: el cambio de código se lee como frase (antes → después, motivo y de dónde viene), no como JSON.
  const ORIGEN: Readonly<Record<string, string>> = {
    MANUAL: 'a mano', REFERENCIA: 'por la referencia del sistema maestro', IMPORTACION: 'por una importación', CAMBIO_MAESTRO: 'por un cambio de sistema maestro',
  };
  const codigoNegocio = (c: Record<string, unknown>): ReactNode => (
    <div className="pequena">
      <span className="mono">{String(c['antes'])}</span> → <span className="mono">{String(c['despues'])}</span>
      {typeof c['origen'] === 'string' && ORIGEN[c['origen']] !== undefined && <> · {ORIGEN[c['origen']]}</>}
      {typeof c['motivo'] === 'string' && <div>Motivo: {c['motivo']}</div>}
    </div>
  );
  if (eventos.length === 0) return <p className="suave">Sin eventos.</p>;
  //  Estabilización post-F13: la configuración en almacén se guarda como foto (mínimo, máximo…); se enseña como
  //  «Máximo: 100 → 30» comparándola con la foto anterior del MISMO almacén.
  const previoDe = new Map<string, unknown>();
  const cronologico = [...eventos].sort((a, b) => a.ocurridoEn.localeCompare(b.ocurridoEn));
  const ultimaPorAlmacen = new Map<string, unknown>();
  for (const e of cronologico) {
    if (e.tipo !== 'ALMACEN' || e.almacen === null) continue;
    if (ultimaPorAlmacen.has(e.almacen)) previoDe.set(e.id, ultimaPorAlmacen.get(e.almacen));
    ultimaPorAlmacen.set(e.almacen, e.cambios);
  }
  return (
    <ol className="lista-historial">
      {eventos.map((e) => (
        <li key={e.id}>
          <strong>{TEXTO[e.tipo] ?? e.tipo}</strong>{e.almacen !== null && ` · ${e.almacen}`}{e.unidad !== null && ` · ${e.unidad}`}
          {e.cambios !== null && (e.tipo === 'CODIGO_NEGOCIO' ? codigoNegocio(e.cambios)
            : <CambiosEvento cambios={e.cambios} previo={previoDe.get(e.id)} />)}
          <div className="suave pequena">{e.usuario.nombre} · {fechaHora(e.ocurridoEn)}</div>
        </li>
      ))}
    </ol>
  );
}

// ================================================================ artículos

export function PaginaArticulos(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [q, setQ] = useState({ texto: '', idTipo: '', idFamilia: '', idSubfamilia: '', inventariable: '', activo: '' });
  const [alta, setAlta] = useState(false);
  const opciones = useDatos(() => api.get<OpcionesInventario>('/api/inventario/opciones'), []);
  const lista = useDatos(() => {
    const p = new URLSearchParams({ porPagina: '200' });
    for (const [k, v] of Object.entries(q)) if (v !== '') p.set(k, v);
    return api.get<{ filas: ArticuloResumen[]; total: number }>(`/api/inventario/articulos?${p.toString()}`);
  }, [q.texto, q.idTipo, q.idFamilia, q.idSubfamilia, q.inventariable, q.activo]);
  const o = opciones.datos;
  const familias = (o?.familias ?? []).filter((f) => q.idTipo === '' || f.tipo.id === q.idTipo);
  const subfamilias = (o?.subfamilias ?? []).filter((s) => q.idFamilia !== '' && s.familia.id === q.idFamilia);
  const contador = ['idTipo', 'idFamilia', 'idSubfamilia', 'inventariable', 'activo'].filter((k) => q[k as keyof typeof q] !== '').length;
  const fijar = (k: keyof typeof q, v: string) => setQ((a) => ({ ...a, [k]: v, ...(k === 'idTipo' ? { idFamilia: '', idSubfamilia: '' } : k === 'idFamilia' ? { idSubfamilia: '' } : {}) }));

  return (
    <Shell dominio="articulos" migas={[{ texto: 'Inventario' }, { texto: 'Artículos' }]} titulo="Artículos"
      acciones={puede('articulos:crear') || puede('importaciones_maestras:gestionar') ? (
        <div className="fila">
          {puede('importaciones_maestras:gestionar') && <Link className="boton" to="/inventario/importaciones/nueva?tipo=ARTICULOS">Importar</Link>}
          {puede('articulos:crear') && <button className="principal" onClick={() => setAlta(true)}>Nuevo artículo</button>}
        </div>
      ) : undefined}>
      <div className="panel">
        <div className="panel-cuerpo">
          <FiltrosResponsive contador={contador}
            busqueda={<input type="search" aria-label="Buscar artículo" placeholder="Código, código ERP, código de barras o descripción…" value={q.texto} onChange={(ev) => fijar('texto', ev.target.value)} />}>
            <div className="fila" style={{ flexWrap: 'wrap' }}>
              <label className="campo"><span>Tipo</span>
                <select value={q.idTipo} onChange={(ev) => fijar('idTipo', ev.target.value)}>
                  <option value="">Todos</option>
                  {(o?.tipos ?? []).map((t) => <option key={t.id} value={t.id}>{t.descripcion}</option>)}
                </select>
              </label>
              <label className="campo"><span>Familia</span>
                <select value={q.idFamilia} onChange={(ev) => fijar('idFamilia', ev.target.value)}>
                  <option value="">Todas</option>
                  {familias.map((f) => <option key={f.id} value={f.id}>{f.descripcion}{q.idTipo === '' ? ` (${f.tipo.descripcion})` : ''}</option>)}
                </select>
              </label>
              <label className="campo"><span>Subfamilia</span>
                <select value={q.idSubfamilia} disabled={q.idFamilia === ''} onChange={(ev) => fijar('idSubfamilia', ev.target.value)}>
                  <option value="">{q.idFamilia === '' ? 'Elige antes una familia' : 'Todas'}</option>
                  {subfamilias.map((s) => <option key={s.id} value={s.id}>{s.descripcion}</option>)}
                </select>
              </label>
              <label className="campo"><span>Inventariable</span>
                <select value={q.inventariable} onChange={(ev) => fijar('inventariable', ev.target.value)}>
                  <option value="">Todos</option><option value="true">Sí</option><option value="false">No</option>
                </select>
              </label>
              <label className="campo"><span>Estado</span>
                <select value={q.activo} onChange={(ev) => fijar('activo', ev.target.value)}>
                  <option value="">Activos</option><option value="false">Inactivos</option><option value="TODOS">Todos</option>
                </select>
              </label>
            </div>
          </FiltrosResponsive>
        </div>
      </div>
      <div className="panel">
        <Panel estado={lista}>
          {(d) => (d.filas.length === 0 ? (
            <Vacio titulo={contador === 0 && q.texto === '' ? 'Aún no hay artículos' : 'Ningún artículo coincide con los filtros'} />
          ) : (
            <>
              <div className="tabla-ots-desktop tabla-desliza">
                <table>
                  <thead><tr><th>Código</th><th>Descripción</th><th>Tipo</th><th>Familia</th><th>Subfamilia</th><th>Unidad</th><th>Inventariable</th><th>Estado</th></tr></thead>
                  <tbody>
                    {d.filas.map((a) => (
                      <tr key={a.id}>
                        <td><Link to={`/inventario/articulos/${a.id}`}><CodigoArticulo articulo={a} /></Link></td>
                        <td>{a.descripcion}</td><td>{a.tipo.descripcion}</td><td>{a.familia.descripcion}</td>
                        <td>{a.subfamilia?.descripcion ?? '—'}</td><td>{a.unidadStock.codigo}</td>
                        <td><SiNo v={a.inventariable} /></td><td><Activo v={a.activo} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="lista-ots-movil" aria-label="Artículos">
                {d.filas.map((a) => (
                  <li key={a.id}>
                    <Link to={`/inventario/articulos/${a.id}`} className="tarjeta-ot tarjeta-parte" aria-label={`${codigoVisible(a)} ${a.descripcion}`}>
                      <div className="tarjeta-ot-cabecera"><span className="tarjeta-ot-codigo"><CodigoArticulo articulo={a} enLinea /></span><Activo v={a.activo} /></div>
                      <div className="tarjeta-ot-datos">
                        <span className="tarjeta-ot-fecha">{a.descripcion}</span>
                        <span className="tarjeta-ot-secundario">{a.tipo.descripcion} · {a.familia.descripcion}{a.subfamilia !== null ? ` · ${a.subfamilia.descripcion}` : ''} · {a.unidadStock.codigo}</span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ))}
        </Panel>
      </div>
      {alta && o !== null && <FormArticulo opciones={o} cerrar={() => setAlta(false)} alGuardar={(id) => navegar(`/inventario/articulos/${id}`)} />}
    </Shell>
  );
}

function FormArticulo({ opciones: o, articulo, cerrar, alGuardar }: { opciones: OpcionesInventario; articulo?: ArticuloDetalle; cerrar: () => void; alGuardar: (id: string) => void }): ReactNode {
  const edita = articulo !== undefined;
  const [v, setV] = useState({
    descripcion: articulo?.descripcion ?? '', idTipo: articulo?.tipo.id ?? '', idFamilia: articulo?.familia.id ?? '',
    idSubfamilia: articulo?.subfamilia?.id ?? '', idUnidadStock: articulo?.unidadStock.id ?? (o.unidades.find((u) => u.codigo === 'UD')?.id ?? ''),
    inventariable: articulo?.inventariable ?? true, controlaLotes: articulo?.controlaLotes ?? false,
    controlaSeries: articulo?.controlaSeries ?? false, controlaCaducidad: articulo?.controlaCaducidad ?? false,
  });
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);
  //  Encadenado: cambiar el tipo vacía familia y subfamilia; cambiar la familia vacía la subfamilia.
  const familias = o.familias.filter((f) => f.tipo.id === v.idTipo);
  const subfamilias = o.subfamilias.filter((s) => s.familia.id === v.idFamilia);
  const poner = (p: Partial<typeof v>) => setV((a) => {
    const n = { ...a, ...p };
    if (p.idTipo !== undefined && p.idTipo !== a.idTipo) { n.idFamilia = ''; n.idSubfamilia = ''; }
    if (p.idFamilia !== undefined && p.idFamilia !== a.idFamilia) n.idSubfamilia = '';
    if (!n.inventariable) { n.controlaLotes = false; n.controlaSeries = false; n.controlaCaducidad = false; }
    if (!n.controlaLotes) n.controlaCaducidad = false;
    return n;
  });

  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    const cuerpo = { ...v, idSubfamilia: v.idSubfamilia === '' ? null : v.idSubfamilia };
    try {
      const r = edita
        ? await api.patch<ArticuloDetalle>(`/api/inventario/articulos/${articulo.id}`, { ...cuerpo, versionEsperada: articulo.actualizadoEn })
        : await api.post<ArticuloDetalle>('/api/inventario/articulos', cuerpo);
      alGuardar(r.id);
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }

  const titulo = edita ? `Editar ${codigoVisible(articulo)}` : 'Nuevo artículo';
  //  FASE 9.1-C: lo inactivo no se OFRECE para lo nuevo, pero si es la clasificación ACTUAL del
  //  artículo se conserva visible (marcada) para no perderla al editar otra cosa.
  const actual = (id: string | undefined, enLista: boolean, texto: string): ReactNode =>
    (edita && id !== undefined && !enLista ? <option value={id}>{texto} (Inactivo)</option> : null);
  return (
    <Modal titulo={titulo} cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label={titulo}>
        <div className="campo"><label htmlFor="ar-desc">Descripción</label>
          <input id="ar-desc" required value={v.descripcion} onChange={(ev) => poner({ descripcion: ev.target.value })} /><Campo campos={campos} k="descripcion" /></div>
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo"><label htmlFor="ar-tipo">Tipo</label>
            <select id="ar-tipo" required value={v.idTipo} onChange={(ev) => poner({ idTipo: ev.target.value })}>
              <option value="">Elige…</option>
              {actual(articulo?.tipo.id, o.tipos.some((t) => t.id === articulo?.tipo.id) || v.idTipo !== articulo?.tipo.id, articulo?.tipo.descripcion ?? '')}
              {o.tipos.map((t) => <option key={t.id} value={t.id}>{t.descripcion}</option>)}
            </select><Campo campos={campos} k="idTipo" /></div>
          <div className="campo"><label htmlFor="ar-familia">Familia</label>
            <select id="ar-familia" required value={v.idFamilia} disabled={v.idTipo === ''} onChange={(ev) => poner({ idFamilia: ev.target.value })}>
              <option value="">{v.idTipo === '' ? 'Elige antes el tipo' : familias.length === 0 ? 'Este tipo no tiene familias' : 'Elige…'}</option>
              {v.idFamilia === articulo?.familia.id && actual(articulo.familia.id, familias.some((f) => f.id === articulo.familia.id), articulo.familia.descripcion)}
              {familias.map((f) => <option key={f.id} value={f.id}>{f.descripcion}</option>)}
            </select><Campo campos={campos} k="idFamilia" /></div>
          <div className="campo"><label htmlFor="ar-sub">Subfamilia (opcional)</label>
            <select id="ar-sub" value={v.idSubfamilia} disabled={v.idFamilia === ''} onChange={(ev) => poner({ idSubfamilia: ev.target.value })}>
              <option value="">Sin subfamilia</option>
              {articulo?.subfamilia != null && v.idSubfamilia === articulo.subfamilia.id && actual(articulo.subfamilia.id, subfamilias.some((s) => s.id === articulo.subfamilia!.id), articulo.subfamilia.descripcion)}
              {subfamilias.map((s) => <option key={s.id} value={s.id}>{s.descripcion}</option>)}
            </select><Campo campos={campos} k="idSubfamilia" /></div>
        </div>
        <div className="campo"><label htmlFor="ar-unidad">Unidad de stock</label>
          <select id="ar-unidad" required value={v.idUnidadStock} onChange={(ev) => poner({ idUnidadStock: ev.target.value })}>
            <option value="">Elige…</option>
            {actual(articulo?.unidadStock.id, o.unidades.some((u) => u.id === articulo?.unidadStock.id), `${articulo?.unidadStock.codigo ?? ''} — ${articulo?.unidadStock.descripcion ?? ''}`)}
            {o.unidades.map((u) => <option key={u.id} value={u.id}>{u.codigo} — {u.descripcion}</option>)}
          </select>
          <p className="suave pequena">Unidad canónica: toda cantidad futura del artículo se expresará en ella.</p>
          <Campo campos={campos} k="idUnidadStock" /></div>
        <fieldset className="campo">
          <legend>Control</legend>
          <label className="fila"><input type="checkbox" checked={v.inventariable} onChange={(ev) => poner({ inventariable: ev.target.checked })} /> Inventariable (tendrá stock y movimientos)</label>
          <label className="fila"><input type="checkbox" checked={v.controlaLotes} disabled={!v.inventariable} onChange={(ev) => poner({ controlaLotes: ev.target.checked })} /> Controla lotes</label>
          <label className="fila"><input type="checkbox" checked={v.controlaCaducidad} disabled={!v.controlaLotes} onChange={(ev) => poner({ controlaCaducidad: ev.target.checked })} /> Controla caducidad (por lote)</label>
          <label className="fila"><input type="checkbox" checked={v.controlaSeries} disabled={!v.inventariable} onChange={(ev) => poner({ controlaSeries: ev.target.checked })} /> Controla números de serie</label>
          <p className="suave pequena">Con lotes o series, cada movimiento indica el lote o las series concretas. Se fijan con el primer movimiento: después no se pueden cambiar.</p>
          <Campo campos={campos} k="inventariable" /><Campo campos={campos} k="controlaCaducidad" />
        </fieldset>
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila">
          <button type="submit" className="principal" disabled={enviando}>{enviando ? 'Guardando…' : edita ? 'Guardar cambios' : 'Crear artículo'}</button>
          <button type="button" onClick={cerrar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

export function PaginaArticulo(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'info';
  const art = useDatos(() => api.get<ArticuloDetalle>(`/api/inventario/articulos/${encodeURIComponent(id)}`), [id]);
  const opciones = useDatos(() => api.get<OpcionesInventario>('/api/inventario/opciones'), []);
  const [editar, setEditar] = useState(false);
  const [eliminar, setEliminar] = useState(false);
  const [etiqueta, setEtiqueta] = useState(false);
  const [regularizar, setRegularizar] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const navegar = useNavigate();
  const migas = [{ texto: 'Inventario' }, { texto: 'Artículos', a: '/inventario/articulos' }, { texto: art.datos === null ? '…' : codigoVisible(art.datos) }];

  if (art.error instanceof FalloApi && (art.error.estado === 404 || art.error.estado === 403)) {
    return <Shell migas={migas} titulo="Artículo no disponible"><div className="panel"><Vacio titulo="Este artículo no existe o no tienes acceso"><Link to="/inventario/articulos">Volver</Link></Vacio></div></Shell>;
  }
  const a = art.datos;
  const gestiona = puede('articulos:gestionar');
  async function activar(activo: boolean): Promise<void> {
    if (a === null) return;
    setError(null);
    try {
      await api.patch(`/api/inventario/articulos/${a.id}`, { activo, versionEsperada: a.actualizadoEn });
      art.recargar();
    } catch (e) {
      setError(e);
    }
  }
  const pestanas: [string, string][] = [['info', 'Información'], ['identidad', 'Códigos y ERP'], ['unidades', 'Unidades'], ...(puede('almacenes:leer') ? [['almacenes', 'Almacenes'] as [string, string]] : []),
    ...(puede('stock:leer') ? [['stock', 'Stock'] as [string, string]] : []),
    //  FASE 9.4: pestañas sólo si el artículo controla lotes / series. F13.2: «Lotes» también para quien puede activarlos (regularización).
    ...((a?.controlaLotes === true && puede('lotes:leer')) || (a !== null && !a.controlaLotes && !a.controlaSeries && puede('lotes:regularizar'))
      ? [['lotes', 'Lotes'] as [string, string]] : []),
    ...(a?.controlaSeries === true && puede('series:leer') ? [['series', 'Series'] as [string, string]] : []),
    ...(puede('movimientos_stock:leer') ? [['movimientos', 'Movimientos'] as [string, string]] : []), ['historial', 'Historial']];

  return (
    <Shell dominio="articulos" migas={migas} titulo={a === null ? 'Artículo' : <><span className="mono">{codigoVisible(a)}</span> · {a.descripcion}</>}
      estado={a === null ? undefined : <Activo v={a.activo} />}
      meta={a === null ? undefined : <>
        {!a.inventariable && <span className="etiqueta neutra">No inventariable</span>}
      </>}
      acciones={a !== null && (gestiona || puede('lotes:etiquetas') || puede('inventario_historico:leer')) ? (
        //  UX-1B: Editar es la acción habitual; activar/desactivar y la eliminación (destructiva), en «Más acciones».
        //  F13.7: el kárdex del artículo se abre desde su ficha (pertenece a Movimientos, sin entrada propia en el menú).
        <BarraAcciones etiqueta="Acciones del artículo"
          secundaria={puede('inventario_historico:leer') ? <Link className="boton" to={`/inventario/kardex?idArticulo=${a.id}`}>Ver kárdex</Link> : undefined}
          principal={!gestiona ? (puede('lotes:etiquetas') ? <button onClick={() => setEtiqueta(true)}>Imprimir etiqueta</button> : undefined)
            : a.activo ? <button className="principal" onClick={() => setEditar(true)}>Editar</button>
            : <button className="principal" onClick={() => void activar(true)}>Reactivar</button>}
          mas={[
            gestiona && puede('lotes:etiquetas') && { texto: 'Imprimir etiqueta', alPulsar: () => setEtiqueta(true) },
            gestiona && a.activo && { texto: 'Desactivar', alPulsar: () => void activar(false) },
            gestiona && puede('articulos:eliminar') && a.eliminable && { texto: 'Eliminar definitivamente', alPulsar: () => setEliminar(true), peligro: true },
          ]} />
      ) : undefined}
      tabs={<Pestanas opciones={pestanas} actual={tab} fijar={(k) => setParams({ tab: k })} />}>
      {error !== null && <ErrorCaja error={error} />}
      <Panel estado={art}>
        {(d) => (
          <>
            {tab === 'info' && (
              <div className="panel">
                <div className="panel-cuerpo datos">
                  <Dato e="Código" v={<span className="mono">{d.codigoNegocio}</span>} />
                  <Dato e="Tipo" v={conEstado(d.tipo.descripcion, d.clasificacionActiva.tipo)} />
                  <Dato e="Familia" v={conEstado(d.familia.descripcion, d.clasificacionActiva.familia)} />
                  <Dato e="Subfamilia" v={d.subfamilia === null ? null : conEstado(d.subfamilia.descripcion, d.clasificacionActiva.subfamilia)} />
                  <Dato e="Unidad de stock" v={conEstado(`${d.unidadStock.codigo} — ${d.unidadStock.descripcion}`, d.clasificacionActiva.unidad)} />
                  <Dato e="Inventariable" v={<SiNo v={d.inventariable} />} />
                  <Dato e="Trazabilidad" v={[d.controlaLotes && 'Lotes', d.controlaCaducidad && 'Caducidad', d.controlaSeries && 'Series'].filter(Boolean).join(' · ') || 'Sin trazabilidad'} />
                  <Dato e="Alta" v={`${d.creadoPor.nombre} · ${fechaHora(d.creadoEn)}`} />
                </div>
                {/* F13.5-A: el token NeatOS (etiquetas, soporte) no se muestra por defecto. */}
                <div className="panel-cuerpo"><DetallesTecnicos token={d.tokenNeatos ?? d.codigo} sistema={d.sistemaCodigoNegocio ?? null} /></div>
              </div>
            )}
            {tab === 'identidad' && <SeccionIdentidad articulo={d} alCambiar={art.recargar} />}
            {tab === 'unidades' && <SeccionUnidades articulo={d} unidades={opciones.datos?.unidades ?? []} gestiona={gestiona && d.activo} alCambiar={art.recargar} />}
            {tab === 'almacenes' && <SeccionAlmacenesArticulo articulo={d} />}
            {tab === 'stock' && puede('stock:leer') && <TablaStock idArticulo={d.id} />}
            {tab === 'lotes' && d.controlaLotes && puede('lotes:leer') && <ListaLotes idArticulo={d.id} articulo={{ ...d, controlaCaducidad: d.controlaCaducidad }} />}
            {tab === 'lotes' && !d.controlaLotes && puede('lotes:regularizar') && (
              <div className="panel"><div className="panel-cuerpo">
                <h2 className="titulo-seccion">Este artículo no controla lotes</h2>
                <p className="pequena">Puedes activar el control de lotes aunque ya tenga movimientos: el histórico no se modifica y su stock actual se reparte, con un ajuste, en los lotes que indiques.</p>
                {d.controlaSeries ? <p className="nota aviso pequena">Controla números de serie: la activación de lotes no se aplica.</p>
                  : <button type="button" className="principal" onClick={() => setRegularizar(true)}>Activar control de lotes…</button>}
              </div></div>
            )}
            {tab === 'series' && d.controlaSeries && puede('series:leer') && <ListaSeries idArticulo={d.id} />}
            {tab === 'movimientos' && puede('movimientos_stock:leer') && <Ledger idArticulo={d.id} />}
            {tab === 'historial' && <HistorialArticulo id={d.id} />}
          </>
        )}
      </Panel>
      {etiqueta && a !== null && <DialogoEtiquetas elementos={[{ tipo: 'ART', id: a.id, titulo: `${codigoVisible(a)} · ${a.descripcion}` }]} cerrar={() => setEtiqueta(false)} />}
      {regularizar && a !== null && <AsistenteRegularizacion idArticulo={a.id} cerrar={() => setRegularizar(false)} alTerminar={art.recargar} />}
      {editar && a !== null && opciones.datos !== null && (
        <FormArticulo opciones={opciones.datos} articulo={a} cerrar={() => setEditar(false)} alGuardar={() => { setEditar(false); art.recargar(); }} />
      )}
      {eliminar && a !== null && (
        <DialogoEliminarArticulo articulo={a} cerrar={() => setEliminar(false)} alEliminar={() => navegar('/inventario/articulos', { replace: true })} />
      )}
    </Shell>
  );
}

/**
 * FASE 9.1-C — confirmación FUERTE: sólo se llega aquí con un artículo virgen y
 * permiso de ADMIN, y aun así hay que teclear su código. Si la base detecta
 * cualquier uso (también futuro: ledger, PT…), responde 409 y se sugiere desactivar.
 */
function DialogoEliminarArticulo({ articulo: a, cerrar, alEliminar }: { articulo: ArticuloDetalle; cerrar: () => void; alEliminar: () => void }): ReactNode {
  const [codigo, setCodigo] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  async function confirmar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      await api.del(`/api/inventario/articulos/${a.id}`);
      alEliminar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo={`Eliminar ${codigoVisible(a)}`} cerrar={cerrar}>
      <form onSubmit={(ev) => void confirmar(ev)} aria-label={`Eliminar ${codigoVisible(a)}`}>
        <p><strong>Este artículo nunca ha tenido uso operativo. Se eliminará definitivamente.</strong></p>
        <p className="suave pequena">No se puede deshacer. Si sólo quieres retirarlo de la operativa, usa «Desactivar».</p>
        <div className="campo">
          <label htmlFor="el-codigo">Escribe {codigoVisible(a)} para confirmar</label>
          <input id="el-codigo" autoComplete="off" value={codigo} onChange={(ev) => setCodigo(ev.target.value)} />
        </div>
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila">
          <button type="submit" className="peligro" disabled={codigo.trim().toUpperCase() !== codigoVisible(a).toUpperCase() || enviando}>{enviando ? 'Eliminando…' : 'Eliminar definitivamente'}</button>
          <button type="button" onClick={cerrar}>Cancelar</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- F13.1 · códigos y referencias ERP

const TIPOS_CODIGO: readonly (readonly [TipoCodigoArticulo, string])[] = [
  ['EAN13', 'EAN-13'], ['EAN8', 'EAN-8'], ['UPCA', 'UPC-A'], ['GTIN14', 'GTIN-14'], ['CODE128', 'Code 128'], ['CODE39', 'Code 39'],
  ['QR', 'QR'], ['DATAMATRIX', 'Data Matrix'], ['OTRO', 'Otro'],
];
const ETIQUETA_TIPO_CODIGO = Object.fromEntries(TIPOS_CODIGO) as Record<TipoCodigoArticulo, string>;

/**
 * F13.1 — referencias en sistemas externos y códigos de barras del artículo.
 * Lo normal es que los cree la importación del ERP; aquí se consultan y, como
 * excepción, se corrigen (añadir, marcar principal, desactivar). Nunca se
 * editan: se desactivan y se crea otro.
 */
function SeccionIdentidad({ articulo: a, alCambiar }: { articulo: ArticuloDetalle; alCambiar: () => void }): ReactNode {
  const { puede, usuario } = useSesion();
  const ident = useDatos(() => api.get<IdentidadArticulo>(`/api/inventario/articulos/${a.id}/identidad`), [a.id]);
  const sistemas = useDatos(() => api.get<{ filas: SistemaExterno[] }>('/api/inventario/sistemas-externos'), []);
  //  F13.3.1: tipo '' = automático (lo deduce NeatOS). Elegirlo es una opción técnica, nunca un paso obligatorio.
  const [codigo, setCodigo] = useState<{ tipo: TipoCodigoArticulo | ''; valor: string; principal: boolean; idUnidad: string }>({ tipo: '', valor: '', principal: false, idUnidad: '' });
  const [ref, setRef] = useState({ idSistema: '', codigoExterno: '' });
  const [campos, setCampos] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const [baja, setBaja] = useState<{ id: string; texto: string } | null>(null);
  const [motivo, setMotivo] = useState('');
  const gestionaCodigos = puede('articulos:codigos') && a.activo;
  const esAdmin = usuario?.roles.some((r) => r.rol === 'ADMIN') === true;
  const gestionaRefs = puede('articulos:referencias') && a.activo;
  const unidadesPresentacion = [a.unidadStock, ...a.conversiones.filter((c) => c.activo).map((c) => c.unidad)];

  async function ejecutar(f: () => Promise<unknown>): Promise<boolean> {
    setError(null); setCampos({}); setOcupado(true);
    try {
      await f();
      ident.recargar(); alCambiar();
      return true;
    } catch (e) {
      setError(e); setCampos(e instanceof FalloApi ? e.erroresDeCampo ?? {} : {});
      return false;
    } finally {
      setOcupado(false);
    }
  }
  async function anadirCodigo(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    const ok = await ejecutar(() => api.post(`/api/inventario/articulos/${a.id}/codigos`, {
      ...(codigo.tipo === '' ? {} : { tipo: codigo.tipo }), valor: codigo.valor, principal: codigo.principal, ...(codigo.idUnidad === '' ? {} : { idUnidad: codigo.idUnidad }),
    }));
    if (ok) setCodigo({ tipo: '', valor: '', principal: false, idUnidad: '' });
  }
  async function anadirRef(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    if (await ejecutar(() => api.post(`/api/inventario/articulos/${a.id}/referencias`, ref))) setRef({ idSistema: '', codigoExterno: '' });
  }

  return (
    <>
      {error !== null && <ErrorCaja error={error} />}
      <Panel estado={ident}>
        {(d) => (
          <>
            <div className="panel">
              <div className="panel-cabecera"><h2>Código del artículo</h2></div>
              <div className="panel-cuerpo">
                <p><span className="mono" style={{ fontSize: '1.15rem', fontWeight: 700 }}>{d.codigoNegocio ?? a.codigoNegocio}</span>{' '}
                  <span className="suave pequena">{(d.sistemaCodigoNegocio ?? null) === null ? 'Sin sistema maestro: es el código de NeatOS.' : `Del sistema maestro ${String(d.sistemaCodigoNegocio)}.`}</span></p>
                {(d.historialCodigoNegocio ?? []).length > 0 && (
                  <div className="tabla-envoltura">
                    <table className="tabla" aria-label="Historial del código">
                      <thead><tr><th>Fecha</th><th>Antes</th><th>Después</th><th>Motivo</th><th>Usuario</th></tr></thead>
                      <tbody>
                        {(d.historialCodigoNegocio ?? []).map((h, i) => (
                          <tr key={i}>
                            <td data-label="Fecha">{fechaHora(h.cambiadoEn)}</td>
                            <td data-label="Antes" className="mono">{h.antes}</td>
                            <td data-label="Después" className="mono">{h.despues}</td>
                            <td data-label="Motivo" className="pequena">{h.motivo ?? '—'}</td>
                            <td data-label="Usuario">{h.usuario}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
            <div className="panel">
              <div className="panel-cabecera"><h2>Referencias en sistemas externos</h2></div>
              <div className="panel-cuerpo">
                <p className="suave pequena">Las crea la importación del catálogo. La del sistema maestro de artículos es el código del artículo; las demás sirven para encontrarlo y para la interoperabilidad.</p>
                {d.referencias.length === 0 ? <Vacio titulo="Sin referencias externas" /> : (
                  <div className="tabla-desliza">
                    <table>
                      <thead><tr><th>Sistema</th><th>Código</th><th>Origen</th><th>Estado</th>{gestionaRefs && <th aria-label="Acciones" />}</tr></thead>
                      <tbody>
                        {d.referencias.map((r) => (
                          <tr key={r.id}>
                            <td>{r.sistema.nombre} <span className="suave mono pequena">{r.sistema.codigo}</span>{r.sistema.esMaestro && <> <span className="etiqueta info">Maestro</span></>}</td>
                            <td className="mono">{r.codigoExterno}</td>
                            <td>{r.origen === 'IMPORTACION' ? `Importación${r.importacion === null ? '' : ` ${r.importacion}`}` : 'Manual'}</td>
                            <td>{r.activo ? <span className="etiqueta exito">Activa</span> : <span className="etiqueta neutra" title={r.motivoDesactivacion ?? undefined}>Desactivada</span>}</td>
                            {gestionaRefs && <td>{r.activo && <button onClick={() => { setMotivo(''); setBaja({ id: r.id, texto: `${r.sistema.codigo} ${r.codigoExterno}` }); }}>Desactivar</button>}</td>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {gestionaRefs && (
                  <details style={{ marginTop: 12 }}>
                    <summary>Añadir referencia manual (excepción o reconciliación)</summary>
                    <form className="fila" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }} onSubmit={(ev) => void anadirRef(ev)}>
                      <label className="campo"><span>Sistema</span>
                        <select value={ref.idSistema} onChange={(ev) => setRef({ ...ref, idSistema: ev.target.value })} required>
                          <option value="">Elige…</option>
                          {(sistemas.datos?.filas ?? []).filter((s) => s.activo).map((s) => <option key={s.id} value={s.id}>{s.nombre} ({s.codigo})</option>)}
                        </select>
                      </label>
                      <label className="campo"><span>Código en ese sistema</span>
                        <input value={ref.codigoExterno} maxLength={80} required onChange={(ev) => setRef({ ...ref, codigoExterno: ev.target.value })} />
                      </label>
                      <button type="submit" disabled={ocupado}>Añadir referencia</button>
                    </form>
                  </details>
                )}
              </div>
            </div>
            <div className="panel">
              <div className="panel-cabecera"><h2>Códigos de barras e identificadores</h2></div>
              <div className="panel-cuerpo">
                {d.codigos.length === 0 ? <Vacio titulo="Sin códigos de barras" /> : (
                  <div className="tabla-desliza">
                    <table>
                      <thead><tr><th>Tipo</th><th>Código</th><th>Presentación</th><th>Origen</th><th>Estado</th>{gestionaCodigos && <th aria-label="Acciones" />}</tr></thead>
                      <tbody>
                        {d.codigos.map((c) => (
                          <tr key={c.id}>
                            <td>{ETIQUETA_TIPO_CODIGO[c.tipo]}{c.principal && <> <span className="etiqueta info">Principal</span></>}</td>
                            <td className="mono">{c.valor}</td>
                            <td>{c.unidad?.codigo ?? a.unidadStock.codigo}</td>
                            <td>{c.origen === 'IMPORTACION' ? `Importación${c.sistema === null ? '' : ` ${c.sistema}`}` : 'Manual'}</td>
                            <td>{c.activo ? <span className="etiqueta exito">Activo</span> : <span className="etiqueta neutra">Desactivado</span>}</td>
                            {gestionaCodigos && (
                              <td>
                                {c.activo && (
                                  <div className="fila">
                                    {!c.principal && <button disabled={ocupado} onClick={() => void ejecutar(() => api.patch(`/api/inventario/articulos/${a.id}/codigos/${c.id}`, { principal: true }))}>Hacer principal</button>}
                                    <button disabled={ocupado} onClick={() => void ejecutar(() => api.patch(`/api/inventario/articulos/${a.id}/codigos/${c.id}`, { activo: false }))}>Desactivar</button>
                                  </div>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {gestionaCodigos && (
                  <form className="fila" style={{ flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }} onSubmit={(ev) => void anadirCodigo(ev)} aria-label="Añadir código de barras">
                    <label className="campo"><span>Código (escríbelo o léelo con la pistola)</span>
                      <input className="mono" value={codigo.valor} required maxLength={200} aria-invalid={campos['valor'] !== undefined}
                        onChange={(ev) => setCodigo({ ...codigo, valor: ev.target.value })} />
                      <Campo campos={campos} k="valor" />
                    </label>
                    <label className="campo"><span>Presentación</span>
                      <select value={codigo.idUnidad} onChange={(ev) => setCodigo({ ...codigo, idUnidad: ev.target.value })}>
                        <option value="">{a.unidadStock.codigo} (unidad de stock)</option>
                        {unidadesPresentacion.slice(1).map((u) => <option key={u.id} value={u.id}>{u.codigo}</option>)}
                      </select>
                    </label>
                    <label className="fila"><input type="checkbox" checked={codigo.principal} onChange={(ev) => setCodigo({ ...codigo, principal: ev.target.checked })} /> Principal</label>
                    <button type="submit" disabled={ocupado}>Añadir código</button>
                    {esAdmin && (
                      <details style={{ flexBasis: '100%' }}>
                        <summary className="pequena">Opciones técnicas</summary>
                        <label className="campo" style={{ maxWidth: 260 }}><span>Tipo técnico del código</span>
                          <select value={codigo.tipo} onChange={(ev) => setCodigo({ ...codigo, tipo: ev.target.value as TipoCodigoArticulo | '' })}>
                            <option value="">Automático (recomendado)</option>
                            {TIPOS_CODIGO.map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                          </select>
                        </label>
                      </details>
                    )}
                  </form>
                )}
              </div>
            </div>
          </>
        )}
      </Panel>
      {baja !== null && (
        <Modal titulo={`Desactivar la referencia ${baja.texto}`} cerrar={() => setBaja(null)}
          pie={<>
            <button onClick={() => setBaja(null)}>Cancelar</button>
            <button className="peligro" disabled={ocupado || motivo.trim().length < 3}
              onClick={() => void ejecutar(() => api.post(`/api/inventario/articulos/${a.id}/referencias/${baja.id}/desactivar`, { motivo })).then((ok) => { if (ok) setBaja(null); })}>Desactivar</button>
          </>}>
          <p>La referencia queda en el historial; el artículo deja de identificarse con ese código en las próximas importaciones.</p>
          <label className="campo"><span>Motivo</span><input value={motivo} maxLength={300} onChange={(ev) => setMotivo(ev.target.value)} /></label>
        </Modal>
      )}
    </>
  );
}

function HistorialArticulo({ id }: { id: string }): ReactNode {
  const h = useHistorial<EventoInventario>(`/api/inventario/articulos/${id}/historial`);
  return <div className="panel"><div className="panel-cuerpo"><Panel estado={h}>{(x) => <Historial eventos={x.filas} />}</Panel><PieHistorial historial={h} /></div></div>;
}

function SeccionUnidades({ articulo: a, unidades, gestiona, alCambiar }: { articulo: ArticuloDetalle; unidades: readonly UnidadMedida[]; gestiona: boolean; alCambiar: () => void }): ReactNode {
  const [idUnidad, setIdUnidad] = useState('');
  const [factor, setFactor] = useState('');
  const [compra, setCompra] = useState(false);
  const [consumo, setConsumo] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);
  const ocupadas = new Set([a.unidadStock.id, ...a.conversiones.filter((c) => c.activo).map((c) => c.unidad.id)]);
  const libres = unidades.filter((u) => !ocupadas.has(u.id));
  async function accion(f: () => Promise<unknown>): Promise<void> {
    setError(null);
    try {
      await f();
      alCambiar();
    } catch (e) {
      setError(e);
    }
  }
  return (
    <section className="panel" aria-label="Unidades">
      <div className="panel-cuerpo">
        <p><strong>Unidad de stock: {a.unidadStock.codigo}</strong> <span className="suave">({a.unidadStock.descripcion})</span></p>
        {a.conversiones.length === 0 ? <p className="suave">Sin unidades alternativas.</p> : (
          <table aria-label="Conversiones">
            <thead><tr><th>Unidad</th><th>Equivale a</th><th>Uso</th><th>Estado</th>{gestiona && <th />}</tr></thead>
            <tbody>
              {a.conversiones.map((c) => (
                <tr key={c.id}>
                  <td>1 {c.unidad.codigo}</td>
                  <td className="mono">{c.factor} {a.unidadStock.codigo}</td>
                  <td>{[c.esCompra && 'Compra', c.esConsumo && 'Consumo'].filter(Boolean).join(' · ') || '—'}</td>
                  <td><Activo v={c.activo} /></td>
                  {gestiona && (
                    <td>
                      <button className="pequeno" onClick={() => void accion(() => api.patch(`/api/inventario/articulos/${a.id}/unidades/${c.id}`, { activo: !c.activo }))}>
                        {c.activo ? 'Desactivar' : 'Reactivar'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {gestiona && (
          <form className="form-rejilla" aria-label="Añadir conversión"
            onSubmit={(ev) => { ev.preventDefault(); void accion(async () => { await api.post(`/api/inventario/articulos/${a.id}/unidades`, { idUnidad, factor, esCompra: compra, esConsumo: consumo }); setIdUnidad(''); setFactor(''); }); }}>
            <div className="campo">
              <label htmlFor="cv-unidad">Unidad alternativa</label>
              <select id="cv-unidad" value={idUnidad} required onChange={(ev) => setIdUnidad(ev.target.value)}>
                <option value="">Elige…</option>
                {libres.map((u) => <option key={u.id} value={u.id}>{u.codigo} — {u.descripcion}</option>)}
              </select><Campo campos={campos} k="idUnidad" />
            </div>
            <div className="campo">
              <label htmlFor="cv-factor">Factor (en {a.unidadStock.codigo})</label>
              <input id="cv-factor" inputMode="decimal" required value={factor} placeholder="p. ej. 10" onChange={(ev) => setFactor(ev.target.value)} /><Campo campos={campos} k="factor" />
            </div>
            <label className="casilla"><input type="checkbox" checked={compra} onChange={(ev) => setCompra(ev.target.checked)} /> Compra</label>
            <label className="casilla"><input type="checkbox" checked={consumo} onChange={(ev) => setConsumo(ev.target.checked)} /> Consumo</label>
            <div className="acciones-rejilla"><button type="submit" disabled={idUnidad === '' || factor.trim() === ''}>Añadir</button></div>
            {idUnidad !== '' && factor.trim() !== '' && <span className="ayuda-rejilla suave pequena">1 {unidades.find((u) => u.id === idUnidad)?.codigo} = {factor} {a.unidadStock.codigo}</span>}
          </form>
        )}
        {error !== null && <ErrorCaja error={error} />}
      </div>
    </section>
  );
}

function SeccionAlmacenesArticulo({ articulo: a }: { articulo: ArticuloDetalle }): ReactNode {
  const { puede } = useSesion();
  const cfg = useDatos(() => api.get<{ filas: ConfigArticuloAlmacen[] }>(`/api/inventario/articulos/${a.id}/almacenes`), [a.id]);
  const gestiona = puede('almacenes:gestionar') && a.activo;
  const almacenes = useDatos(() => (gestiona ? api.get<{ filas: AlmacenResumen[] }>('/api/inventario/almacenes') : Promise.resolve({ filas: [] as AlmacenResumen[] })), [gestiona]);
  const [editando, setEditando] = useState<ConfigArticuloAlmacen | 'nueva' | null>(null);
  const configurados = new Set((cfg.datos?.filas ?? []).filter((c) => c.activo).map((c) => c.almacen.id));
  const elegibles = (almacenes.datos?.filas ?? []).filter((x) => x.editable && !configurados.has(x.id));
  return (
    <section className="panel" aria-label="Almacenes del artículo">
      <div className="panel-cuerpo">
        <p className="suave pequena">Configuración de reposición por almacén, en {a.unidadStock.codigo}. El stock se mostrará cuando existan movimientos.</p>
        <Panel estado={cfg}>
          {(d) => (d.filas.length === 0 ? <p className="suave">Este artículo no está configurado en ningún almacén.</p> : (
            <div className="tabla-desliza">
              <table aria-label="Configuración por almacén">
                <thead><tr><th>Almacén</th><th>Tipo</th><th>Mínimo</th><th>Máximo</th><th>Punto de pedido</th><th>Inventariable</th><th>Estado</th>{gestiona && <th />}</tr></thead>
                <tbody>
                  {d.filas.map((c) => (
                    <tr key={c.id}>
                      <td><Link to={`/inventario/almacenes/${c.almacen.id}`}>{codigoAlmacen(c.almacen)}</Link> <span className="pequena">{c.almacen.descripcion}</span></td>
                      <td>{TIPOS_ALMACEN[c.almacen.tipo]}{c.almacen.centro !== null ? ` · ${c.almacen.centro}` : ''}</td>
                      <td>{cantidad(c.stockMinimo, a.unidadStock.codigo)}</td><td>{cantidad(c.stockMaximo, a.unidadStock.codigo)}</td><td>{cantidad(c.puntoPedido, a.unidadStock.codigo)}</td>
                      <td><SiNo v={c.inventariable} /></td><td><Activo v={c.activo} /></td>
                      {gestiona && <td>{c.editable && <button className="pequeno" onClick={() => setEditando(c)}>Editar</button>}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Panel>
        {gestiona && elegibles.length > 0 && <button onClick={() => setEditando('nueva')}>Configurar en otro almacén</button>}
      </div>
      {editando !== null && (
        <DialogoConfig articulo={a} config={editando === 'nueva' ? null : editando} almacenes={elegibles}
          cerrar={() => setEditando(null)} alGuardar={() => { setEditando(null); cfg.recargar(); }} />
      )}
    </section>
  );
}

function DialogoConfig({ articulo: a, config, almacenes, cerrar, alGuardar }: {
  articulo: ArticuloDetalle; config: ConfigArticuloAlmacen | null; almacenes: readonly AlmacenResumen[]; cerrar: () => void; alGuardar: () => void;
}): ReactNode {
  const [idAlmacen, setIdAlmacen] = useState(config?.almacen.id ?? '');
  const [n, setN] = useState({ stockMinimo: config?.stockMinimo ?? '', stockMaximo: config?.stockMaximo ?? '', puntoPedido: config?.puntoPedido ?? '' });
  const [activo, setActivo] = useState(config?.activo ?? true);
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);
  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    const niveles = { stockMinimo: n.stockMinimo === '' ? null : n.stockMinimo, stockMaximo: n.stockMaximo === '' ? null : n.stockMaximo, puntoPedido: n.puntoPedido === '' ? null : n.puntoPedido };
    try {
      if (config === null) await api.post(`/api/inventario/articulos/${a.id}/almacenes`, { idAlmacen, ...niveles });
      else await api.patch(`/api/inventario/articulos/${a.id}/almacenes/${config.almacen.id}`, { ...niveles, activo, versionEsperada: config.actualizadoEn });
      alGuardar();
    } catch (e) {
      setError(e);
    }
  }
  const titulo = config === null ? 'Configurar en almacén' : `Configuración en ${codigoAlmacen(config.almacen)}`;
  return (
    <Modal titulo={titulo} cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label={titulo}>
        {config === null && (
          <div className="campo"><label htmlFor="cf-alm">Almacén</label>
            <select id="cf-alm" required value={idAlmacen} onChange={(ev) => setIdAlmacen(ev.target.value)}>
              <option value="">Elige…</option>
              {almacenes.map((x) => <option key={x.id} value={x.id}>{codigoAlmacen(x)} — {x.descripcion}</option>)}
            </select><Campo campos={campos} k="idAlmacen" /></div>
        )}
        <div className="form-rejilla">
          {([['stockMinimo', 'Mínimo'], ['puntoPedido', 'Punto de pedido'], ['stockMaximo', 'Máximo']] as const).map(([k, t]) => (
            <div key={k} className="campo"><label htmlFor={`cf-${k}`}>{t} ({a.unidadStock.codigo})</label>
              <input id={`cf-${k}`} inputMode="decimal" value={n[k]} onChange={(ev) => setN((x) => ({ ...x, [k]: ev.target.value }))} /><Campo campos={campos} k={k} /></div>
          ))}
        </div>
        <p className="suave pequena">Todos opcionales. Si los indicas: mínimo ≤ punto de pedido ≤ máximo.</p>
        {config !== null && <label className="fila"><input type="checkbox" checked={activo} onChange={(ev) => setActivo(ev.target.checked)} /> Configuración activa</label>}
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila"><button type="submit" className="principal">Guardar</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

// ================================================================ almacenes

export function PaginaAlmacenes(): ReactNode {
  const { puede } = useSesion();
  const navegar = useNavigate();
  const [q, setQ] = useState({ texto: '', tipo: '', activo: '' });
  const [alta, setAlta] = useState(false);
  const lista = useDatos(() => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== '') p.set(k, v);
    return api.get<{ filas: AlmacenResumen[] }>(`/api/inventario/almacenes?${p.toString()}`);
  }, [q.texto, q.tipo, q.activo]);
  const opciones = useDatos(() => (puede('almacenes:crear') ? api.get<OpcionesInventario>('/api/inventario/opciones') : Promise.resolve(null)), []);
  const contador = (['tipo', 'activo'] as const).filter((k) => q[k] !== '').length;
  return (
    <Shell dominio="almacenes" migas={[{ texto: 'Inventario' }, { texto: 'Almacenes' }]} titulo="Almacenes"
      acciones={puede('almacenes:crear') || puede('importaciones_maestras:gestionar') ? (
        <div className="fila">
          {puede('importaciones_maestras:gestionar') && <Link className="boton" to="/inventario/importaciones/nueva?tipo=ALMACENES">Importar</Link>}
          {puede('almacenes:crear') && <button className="principal" onClick={() => setAlta(true)}>Nuevo almacén</button>}
        </div>
      ) : undefined}>
      <div className="panel">
        <div className="panel-cuerpo">
          <FiltrosResponsive contador={contador}
            busqueda={<input type="search" aria-label="Buscar almacén" placeholder="Código o descripción…" value={q.texto} onChange={(ev) => setQ((a) => ({ ...a, texto: ev.target.value }))} />}>
            <div className="fila" style={{ flexWrap: 'wrap' }}>
              <label className="campo"><span>Tipo</span>
                <select value={q.tipo} onChange={(ev) => setQ((a) => ({ ...a, tipo: ev.target.value }))}>
                  <option value="">Todos</option>
                  {Object.entries(TIPOS_ALMACEN).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
                </select>
              </label>
              <label className="campo"><span>Estado</span>
                <select value={q.activo} onChange={(ev) => setQ((a) => ({ ...a, activo: ev.target.value }))}>
                  <option value="">Activos</option><option value="false">Inactivos</option><option value="TODOS">Todos</option>
                </select>
              </label>
            </div>
          </FiltrosResponsive>
        </div>
      </div>
      <div className="panel">
        <Panel estado={lista}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo={contador === 0 && q.texto === '' ? 'Aún no hay almacenes' : 'Ningún almacén coincide con los filtros'} /> : (
            <>
              <div className="tabla-ots-desktop tabla-desliza">
                <table>
                  <thead><tr><th>Código</th><th>Descripción</th><th>Tipo</th><th>Centro</th><th>Stock negativo</th><th>Inventariable</th><th>Estado</th></tr></thead>
                  <tbody>
                    {d.filas.map((x) => (
                      <tr key={x.id}>
                        <td className="mono"><Link to={`/inventario/almacenes/${x.id}`}>{codigoAlmacen(x)}</Link></td>
                        <td>{x.descripcion}</td><td>{TIPOS_ALMACEN[x.tipo]}</td><td>{x.centro?.nombre ?? '—'}</td>
                        <td><SiNo v={x.permiteStockNegativo} si="Permitido" no="No" /></td><td><SiNo v={x.inventariable} /></td><td><Activo v={x.activo} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ul className="lista-ots-movil" aria-label="Almacenes">
                {d.filas.map((x) => (
                  <li key={x.id}>
                    <Link to={`/inventario/almacenes/${x.id}`} className="tarjeta-ot tarjeta-parte" aria-label={`${codigoAlmacen(x)} ${x.descripcion}`}>
                      <div className="tarjeta-ot-cabecera"><span className="tarjeta-ot-codigo mono">{codigoAlmacen(x)}</span><Activo v={x.activo} /></div>
                      <div className="tarjeta-ot-datos">
                        <span className="tarjeta-ot-fecha">{x.descripcion}</span>
                        <span className="tarjeta-ot-secundario">{TIPOS_ALMACEN[x.tipo]}{x.centro !== null ? ` · ${x.centro.nombre}` : ''}</span>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ))}
        </Panel>
      </div>
      {alta && opciones.datos !== null && <FormAlmacen opciones={opciones.datos} cerrar={() => setAlta(false)} alGuardar={(id) => navegar(`/inventario/almacenes/${id}`)} />}
    </Shell>
  );
}

/**
 * F13.5-A: el código de negocio de un almacén (FUR-MNT-01, código del ERP…) se cambia a mano, con motivo. No cambia
 * su identidad (uuid ni ALM-…), ni su stock, ni sus etiquetas. Un código que fue de otro almacén exige confirmarlo.
 */
function DialogoCodigoAlmacen({ almacen, cerrar, alGuardar }: { almacen: AlmacenDetalle; cerrar: () => void; alGuardar: () => void }): ReactNode {
  const [codigo, setCodigo] = useState(codigoAlmacen(almacen));
  const [motivo, setMotivo] = useState('');
  const [reutilizar, setReutilizar] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [enviando, setEnviando] = useState(false);
  const campos = erroresDe(error);
  const pideReutilizar = error instanceof FalloApi && error.codigo === 'CODIGO_NEGOCIO_REUTILIZADO';
  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await api.post(`/api/inventario/almacenes/${almacen.id}/codigo-negocio`, {
        codigoNegocio: codigo.trim() === '' ? null : codigo, motivo, ...(reutilizar ? { confirmarReutilizacion: true } : {}),
      });
      alGuardar();
    } catch (e) {
      setError(e);
    } finally {
      setEnviando(false);
    }
  }
  return (
    <Modal titulo={`Cambiar el código de ${almacen.descripcion}`} cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label="Cambiar código del almacén">
        <div className="campo"><label htmlFor="cn-codigo">Código</label>
          <input id="cn-codigo" className="mono" maxLength={80} value={codigo} onChange={(ev) => setCodigo(ev.target.value)} />
          <span className="suave pequena">Vacío = volver al código de NeatOS ({codigoAlmacen(almacen)}). Se conservan mayúsculas, ceros y guiones.</span>
          <Campo campos={campos} k="codigoNegocio" /></div>
        <div className="campo"><label htmlFor="cn-motivo">Motivo</label>
          <input id="cn-motivo" required minLength={3} maxLength={500} value={motivo} onChange={(ev) => setMotivo(ev.target.value)} /><Campo campos={campos} k="motivo" /></div>
        {pideReutilizar && (
          <label className="fila"><input type="checkbox" checked={reutilizar} onChange={(ev) => setReutilizar(ev.target.checked)} /> Sí, reutilizar ese código</label>
        )}
        <p className="suave pequena">No cambia el stock ni las etiquetas ya impresas: siguen identificando este almacén.</p>
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila"><button type="submit" className="principal" disabled={enviando}>{enviando ? 'Guardando…' : 'Guardar código'}</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

function FormAlmacen({ opciones: o, almacen, cerrar, alGuardar }: { opciones: OpcionesInventario; almacen?: AlmacenDetalle; cerrar: () => void; alGuardar: (id: string) => void }): ReactNode {
  const { puede } = useSesion();
  const edita = almacen !== undefined;
  const [v, setV] = useState({
    descripcion: almacen?.descripcion ?? '', tipo: almacen?.tipo ?? ('CENTRAL' as TipoAlmacen), idCentro: almacen?.centro?.id ?? '',
    inventariable: almacen?.inventariable ?? true, permiteStockNegativo: almacen?.permiteStockNegativo ?? false,
    codigoNegocio: '',
  });
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);
  const configuraNegativo = puede('almacenes:configurar_stock_negativo');
  async function enviar(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    setError(null);
    const cuerpo: Record<string, unknown> = { descripcion: v.descripcion, tipo: v.tipo, idCentro: v.idCentro === '' ? null : v.idCentro, inventariable: v.inventariable };
    if (configuraNegativo) cuerpo['permiteStockNegativo'] = v.permiteStockNegativo;
    if (!edita && v.codigoNegocio.trim() !== '') cuerpo['codigoNegocio'] = v.codigoNegocio;
    try {
      const r = edita
        ? await api.patch<AlmacenDetalle>(`/api/inventario/almacenes/${almacen.id}`, { ...cuerpo, versionEsperada: almacen.actualizadoEn })
        : await api.post<AlmacenDetalle>('/api/inventario/almacenes', cuerpo);
      alGuardar(r.id);
    } catch (e) {
      setError(e);
    }
  }
  const titulo = edita ? `Editar ${codigoAlmacen(almacen)}` : 'Nuevo almacén';
  return (
    <Modal titulo={titulo} cerrar={cerrar}>
      <form onSubmit={(ev) => void enviar(ev)} aria-label={titulo}>
        <div className="campo"><label htmlFor="al-desc">Descripción</label>
          <input id="al-desc" required value={v.descripcion} onChange={(ev) => setV((a) => ({ ...a, descripcion: ev.target.value }))} /><Campo campos={campos} k="descripcion" /></div>
        {!edita && (
          <div className="campo"><label htmlFor="al-codigo">Código (opcional)</label>
            <input id="al-codigo" className="mono" maxLength={80} placeholder="FUR-MNT-01, código del ERP…" value={v.codigoNegocio}
              onChange={(ev) => setV((a) => ({ ...a, codigoNegocio: ev.target.value }))} />
            <span className="suave pequena">Sin código, NeatOS le asigna uno (ALM-…).</span><Campo campos={campos} k="codigoNegocio" /></div>
        )}
        <div className="fila" style={{ flexWrap: 'wrap' }}>
          <div className="campo"><label htmlFor="al-tipo">Tipo</label>
            <select id="al-tipo" value={v.tipo} onChange={(ev) => setV((a) => ({ ...a, tipo: ev.target.value as TipoAlmacen }))}>
              {Object.entries(TIPOS_ALMACEN).map(([k, t]) => <option key={k} value={k}>{t}</option>)}
            </select></div>
          <div className="campo"><label htmlFor="al-centro">Centro{v.tipo === 'CENTRO' ? '' : ' (opcional)'}</label>
            <select id="al-centro" required={v.tipo === 'CENTRO'} value={v.idCentro} onChange={(ev) => setV((a) => ({ ...a, idCentro: ev.target.value }))}>
              <option value="">{v.tipo === 'CENTRO' ? 'Elige el centro…' : 'Sin centro'}</option>
              {o.centros.map((c) => <option key={c.id} value={c.id}>{c.cliente} — {c.nombre}</option>)}
            </select><Campo campos={campos} k="idCentro" /></div>
        </div>
        <label className="fila"><input type="checkbox" checked={v.inventariable} onChange={(ev) => setV((a) => ({ ...a, inventariable: ev.target.checked }))} /> Inventariable</label>
        {configuraNegativo && (
          <label className="fila"><input type="checkbox" checked={v.permiteStockNegativo} onChange={(ev) => setV((a) => ({ ...a, permiteStockNegativo: ev.target.checked }))} /> Permitir stock negativo</label>
        )}
        {configuraNegativo && <p className="suave pequena">Sólo se configura la política; se aplicará cuando existan movimientos. El cambio queda en el historial.</p>}
        {error !== null && <ErrorCaja error={error} />}
        <div className="fila"><button type="submit" className="principal">{edita ? 'Guardar cambios' : 'Crear almacén'}</button><button type="button" onClick={cerrar}>Cancelar</button></div>
      </form>
    </Modal>
  );
}

export function PaginaAlmacen(): ReactNode {
  const { id = '' } = useParams();
  const { puede } = useSesion();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'info';
  const alm = useDatos(() => api.get<AlmacenDetalle>(`/api/inventario/almacenes/${encodeURIComponent(id)}`), [id]);
  const opciones = useDatos(() => (puede('almacenes:gestionar') ? api.get<OpcionesInventario>('/api/inventario/opciones') : Promise.resolve(null)), []);
  const [editar, setEditar] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [cambiarCodigo, setCambiarCodigo] = useState(false);
  const migas = [{ texto: 'Inventario' }, { texto: 'Almacenes', a: '/inventario/almacenes' }, { texto: alm.datos === null ? '…' : codigoAlmacen(alm.datos) }];
  if (alm.error instanceof FalloApi && (alm.error.estado === 404 || alm.error.estado === 403)) {
    return <Shell migas={migas} titulo="Almacén no disponible"><div className="panel"><Vacio titulo="Este almacén no existe o no tienes acceso"><Link to="/inventario/almacenes">Volver</Link></Vacio></div></Shell>;
  }
  const x = alm.datos;
  const gestiona = puede('almacenes:gestionar') && x !== null && x.editable;
  async function patch(cuerpo: Record<string, unknown>): Promise<void> {
    if (x === null) return;
    setError(null);
    try {
      await api.patch(`/api/inventario/almacenes/${x.id}`, { ...cuerpo, versionEsperada: x.actualizadoEn });
      alm.recargar();
    } catch (e) {
      setError(e);
    }
  }
  return (
    <Shell dominio="almacenes" migas={migas} titulo={x?.descripcion ?? 'Almacén'}
      subtitulo={x === null ? undefined : <span className="fila" style={{ flexWrap: 'wrap' }}><span className="mono">{codigoAlmacen(x)}</span><span className="etiqueta neutra">{TIPOS_ALMACEN[x.tipo]}</span><Activo v={x.activo} /></span>}
      acciones={gestiona ? (
        <BarraAcciones etiqueta="Acciones del almacén">
          {x.activo && <button onClick={() => setEditar(true)}>Editar</button>}
          <button onClick={() => setCambiarCodigo(true)}>Cambiar código</button>
          <button onClick={() => void patch({ activo: !x.activo })}>{x.activo ? 'Desactivar' : 'Reactivar'}</button>
        </BarraAcciones>
      ) : undefined}
      tabs={<Pestanas opciones={[['info', 'Información'], ['articulos', 'Artículos'], ...(puede('stock:leer') ? [['stock', 'Stock'] as const] : []),
        ...(puede('movimientos_stock:leer') ? [['movimientos', 'Movimientos'] as const] : []), ['historial', 'Historial']]} actual={tab} fijar={(k) => setParams({ tab: k })} />}>
      {error !== null && <ErrorCaja error={error} />}
      <Panel estado={alm}>
        {(d) => (
          <>
            {tab === 'info' && (
              <div className="panel">
                <div className="panel-cuerpo datos">
                  <Dato e="Código" v={<span className="mono">{codigoAlmacen(d)}</span>} />
                  <Dato e="Tipo" v={TIPOS_ALMACEN[d.tipo]} />
                  <Dato e="Centro" v={d.centro === null ? null : `${d.centro.cliente} — ${d.centro.nombre}`} />
                  <Dato e="Inventariable" v={<SiNo v={d.inventariable} />} />
                  <Dato e="Stock negativo" v={<SiNo v={d.permiteStockNegativo} si="Permitido" no="No permitido" />} />
                  <Dato e="Alta" v={`${d.creadoPor.nombre} · ${fechaHora(d.creadoEn)}`} />
                  <Dato e="En otros sistemas" v={(d.referencias ?? []).filter((r) => r.activo).length === 0 ? null : (
                    <ul className="lista-simple" aria-label="Referencias externas del almacén">
                      {(d.referencias ?? []).filter((r) => r.activo).map((r) => (
                        <li key={r.sistema.codigo}>{r.sistema.nombre}: <span className="mono">{r.codigoExterno}</span>{r.maestro && <> <span className="etiqueta info">Maestro</span></>}
                          {r.importacion !== null && <span className="suave pequena"> · {r.importacion}</span>}</li>
                      ))}
                    </ul>
                  )} />
                </div>
                {!d.editable && <div className="panel-cuerpo"><p className="suave pequena">Puedes consultarlo, pero sólo gestionas los almacenes de tus centros.</p></div>}
                <div className="panel-cuerpo"><DetallesTecnicos token={d.codigo} /></div>
              </div>
            )}
            {tab === 'articulos' && <ArticulosDeAlmacen id={d.id} />}
            {tab === 'stock' && puede('stock:leer') && <TablaStock idAlmacen={d.id} />}
            {tab === 'movimientos' && puede('movimientos_stock:leer') && <Ledger idAlmacen={d.id} />}
            {tab === 'historial' && <HistorialAlmacen id={d.id} />}
          </>
        )}
      </Panel>
      {cambiarCodigo && x !== null && <DialogoCodigoAlmacen almacen={x} cerrar={() => setCambiarCodigo(false)} alGuardar={() => { setCambiarCodigo(false); alm.recargar(); }} />}
      {editar && x !== null && opciones.datos !== null && (
        <FormAlmacen opciones={opciones.datos} almacen={x} cerrar={() => setEditar(false)} alGuardar={() => { setEditar(false); alm.recargar(); }} />
      )}
    </Shell>
  );
}

function ArticulosDeAlmacen({ id }: { id: string }): ReactNode {
  const l = useDatos(() => api.get<{ filas: ConfigArticuloAlmacen[] }>(`/api/inventario/almacenes/${id}/articulos`), [id]);
  return (
    <section className="panel" aria-label="Artículos del almacén">
      <div className="panel-cuerpo">
        <p className="suave pequena">Configuración de reposición (no es el saldo: el stock está en la pestaña Stock).</p>
        <Panel estado={l}>
          {(d) => (d.filas.length === 0 ? <p className="suave">Ningún artículo configurado en este almacén.</p> : (
            <div className="tabla-desliza">
              <table aria-label="Artículos configurados">
                <thead><tr><th>Código</th><th>Descripción</th><th>Unidad</th><th>Mínimo</th><th>Máximo</th><th>Punto de pedido</th><th>Estado</th></tr></thead>
                <tbody>
                  {d.filas.map((c) => (
                    <tr key={c.id}>
                      <td className="mono"><Link to={`/inventario/articulos/${c.articulo.id}?tab=almacenes`}>{codigoVisible(c.articulo)}</Link></td>
                      <td>{conEstado(c.articulo.descripcion, c.articulo.activo)}</td><td>{c.articulo.unidad}</td>
                      <td>{c.stockMinimo ?? '—'}</td><td>{c.stockMaximo ?? '—'}</td><td>{c.puntoPedido ?? '—'}</td><td><Activo v={c.activo} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Panel>
      </div>
    </section>
  );
}

function HistorialAlmacen({ id }: { id: string }): ReactNode {
  const h = useHistorial<EventoInventario>(`/api/inventario/almacenes/${id}/historial`);
  return <div className="panel"><div className="panel-cuerpo"><Panel estado={h}>{(x) => <Historial eventos={x.filas} />}</Panel><PieHistorial historial={h} /></div></div>;
}

// ================================================================ maestros

type Maestro = 'unidades' | 'tipos' | 'familias' | 'subfamilias';

export function PaginaMaestrosInventario(): ReactNode {
  const { puede } = useSesion();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') ?? 'unidades') as Maestro;
  //  F13.8: unidades y familias se importan con el mismo flujo que artículos y almacenes.
  const importable = tab === 'unidades' ? 'UNIDADES' : tab === 'familias' ? 'FAMILIAS' : null;
  return (
    <Shell dominio="articulos" migas={[{ texto: 'Inventario' }, { texto: 'Artículos', a: '/inventario/articulos' }, { texto: 'Configuración' }]} titulo="Configuración de Inventario"
      acciones={importable !== null && puede('importaciones_maestras:gestionar')
        ? <Link className="boton" to={`/inventario/importaciones/nueva?tipo=${importable}`}>Importar {tab}</Link> : undefined}
      tabs={<Pestanas opciones={[['unidades', 'Unidades'], ['tipos', 'Tipos de artículo'], ['familias', 'Familias'], ['subfamilias', 'Subfamilias']]} actual={tab} fijar={(k) => setParams({ tab: k })} />}>
      <ListaMaestro key={tab} tipo={tab} />
    </Shell>
  );
}

type FilaMaestro = { id: string; codigo: string; descripcion: string; activo: boolean; decimales?: number; tipo?: { descripcion: string }; familia?: { descripcion: string } };

function ListaMaestro({ tipo }: { tipo: Maestro }): ReactNode {
  const { puede } = useSesion();
  const eliminaDefinitivo = puede('maestros_inventario:eliminar');
  const [aviso, setAviso] = useState<string | null>(null);
  const lista = useDatos(() => api.get<{ filas: FilaMaestro[] }>(`/api/inventario/${tipo}?activo=TODOS`), [tipo]);
  const tipos = useDatos(() => (tipo === 'familias' ? api.get<{ filas: TipoArticulo[] }>('/api/inventario/tipos') : Promise.resolve({ filas: [] as TipoArticulo[] })), [tipo]);
  const familias = useDatos(() => (tipo === 'subfamilias' ? api.get<{ filas: FamiliaArticulo[] }>('/api/inventario/familias') : Promise.resolve({ filas: [] as FamiliaArticulo[] })), [tipo]);
  const [nuevo, setNuevo] = useState({ codigo: '', descripcion: '', decimales: '0', padre: '' });
  const [editando, setEditando] = useState<FilaMaestro | null>(null);
  //  UX-1B: confirmación de la eliminación en un diálogo propio (antes window.confirm).
  const [porEliminar, setPorEliminar] = useState<FilaMaestro | null>(null);
  const [error, setError] = useState<unknown>(null);
  const campos = erroresDe(error);
  async function accion(f: () => Promise<unknown>): Promise<boolean> {
    setError(null);
    try {
      await f();
      lista.recargar();
      return true;
    } catch (e) {
      setError(e);
      return false;
    }
  }
  const padre = tipo === 'familias' ? { campo: 'idTipo', etiqueta: 'Tipo', opciones: (tipos.datos?.filas ?? []) as { id: string; descripcion: string }[] }
    : tipo === 'subfamilias' ? { campo: 'idFamilia', etiqueta: 'Familia', opciones: (familias.datos?.filas ?? []).map((f: FamiliaArticulo | SubfamiliaArticulo) => ({ id: f.id, descripcion: `${f.descripcion} (${'tipo' in f ? f.tipo.descripcion : ''})` })) }
      : null;
  async function crear(ev: FormEvent): Promise<void> {
    ev.preventDefault();
    const cuerpo: Record<string, unknown> = { codigo: nuevo.codigo, descripcion: nuevo.descripcion };
    if (tipo === 'unidades') cuerpo['decimales'] = Number(nuevo.decimales);
    if (padre !== null) cuerpo[padre.campo] = nuevo.padre;
    if (await accion(() => api.post(`/api/inventario/${tipo}`, cuerpo))) setNuevo({ codigo: '', descripcion: '', decimales: '0', padre: nuevo.padre });
  }
  return (
    <div className="panel">
      <div className="panel-cuerpo">
        <form className="fila" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }} aria-label="Nuevo" onSubmit={(ev) => void crear(ev)}>
          {padre !== null && (
            <label className="campo"><span>{padre.etiqueta}</span>
              <select required value={nuevo.padre} onChange={(ev) => setNuevo((a) => ({ ...a, padre: ev.target.value }))}>
                <option value="">Elige…</option>
                {padre.opciones.map((x) => <option key={x.id} value={x.id}>{x.descripcion}</option>)}
              </select><Campo campos={campos} k={padre.campo} />
            </label>
          )}
          <label className="campo"><span>Código</span>
            <input required value={nuevo.codigo} onChange={(ev) => setNuevo((a) => ({ ...a, codigo: ev.target.value.toUpperCase() }))} /><Campo campos={campos} k="codigo" /></label>
          <label className="campo"><span>Descripción</span>
            <input required value={nuevo.descripcion} onChange={(ev) => setNuevo((a) => ({ ...a, descripcion: ev.target.value }))} /><Campo campos={campos} k="descripcion" /></label>
          {tipo === 'unidades' && (
            <label className="campo"><span>Decimales</span>
              <input type="number" min={0} max={6} value={nuevo.decimales} onChange={(ev) => setNuevo((a) => ({ ...a, decimales: ev.target.value }))} /></label>
          )}
          <button type="submit" className="principal">Añadir</button>
        </form>
        <p className="suave pequena">El código es inmutable. Lo que ya no se use se desactiva (sigue visible en el histórico).
          {eliminaDefinitivo && ' «Eliminar» sólo borra lo creado por error que nunca se ha usado; si está en uso, se rechaza y hay que desactivarlo.'}</p>
        {aviso !== null && <div className="nota exito" role="status">{aviso}</div>}
        {error !== null && <ErrorCaja error={error} />}
        <Panel estado={lista}>
          {(d) => (d.filas.length === 0 ? <Vacio titulo="Todavía no hay registros" /> : (
            <div className="tabla-desliza">
              <table aria-label="Registros">
                <thead><tr><th>Código</th><th>Descripción</th>{tipo === 'unidades' && <th>Decimales</th>}{tipo === 'familias' && <th>Tipo</th>}{tipo === 'subfamilias' && <th>Familia</th>}<th>Estado</th><th /></tr></thead>
                <tbody>
                  {d.filas.map((f) => (
                    <tr key={f.id}>
                      <td className="mono">{f.codigo}</td><td>{f.descripcion}</td>
                      {tipo === 'unidades' && <td>{f.decimales}</td>}
                      {tipo === 'familias' && <td>{f.tipo?.descripcion}</td>}
                      {tipo === 'subfamilias' && <td>{f.familia?.descripcion}</td>}
                      <td><Activo v={f.activo} /></td>
                      <td className="fila">
                        <button className="pequeno" onClick={() => setEditando(f)}>Editar</button>
                        <button className="pequeno" onClick={() => void accion(() => api.patch(`/api/inventario/${tipo}/${f.id}`, { activo: !f.activo }))}>{f.activo ? 'Desactivar' : 'Reactivar'}</button>
                        {eliminaDefinitivo && (
                          <button className="pequeno peligro" onClick={() => setPorEliminar(f)}>Eliminar</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Panel>
      </div>
      {porEliminar !== null && (
        <ConfirmDialog titulo={`Eliminar ${porEliminar.codigo}`} confirmar="Eliminar definitivamente" cerrar={() => setPorEliminar(null)}
          descripcion={<p>{porEliminar.codigo} · {porEliminar.descripcion}</p>}
          consecuencia="Es definitivo. Sólo es posible si nunca se ha usado; si está en uso, se rechazará y habrá que desactivarlo."
          alConfirmar={() => {
            const f = porEliminar;
            setPorEliminar(null);
            setAviso(null);
            void accion(() => api.del(`/api/inventario/${tipo}/${f.id}`)).then((ok) => { if (ok) setAviso(`${f.codigo} eliminado definitivamente.`); });
          }} />
      )}
      {editando !== null && (
        <Modal titulo={`Editar ${editando.codigo}`} cerrar={() => setEditando(null)}>
          <EditarMaestro fila={editando} unidades={tipo === 'unidades'} guardar={async (cuerpo) => { if (await accion(() => api.patch(`/api/inventario/${tipo}/${editando.id}`, cuerpo))) setEditando(null); }} cerrar={() => setEditando(null)} />
        </Modal>
      )}
    </div>
  );
}

function EditarMaestro({ fila, unidades, guardar, cerrar }: { fila: FilaMaestro; unidades: boolean; guardar: (c: Record<string, unknown>) => Promise<void>; cerrar: () => void }): ReactNode {
  const [desc, setDesc] = useState(fila.descripcion);
  const [dec, setDec] = useState(String(fila.decimales ?? 0));
  return (
    <form aria-label={`Editar ${fila.codigo}`} onSubmit={(ev) => { ev.preventDefault(); void guardar(unidades ? { descripcion: desc, decimales: Number(dec) } : { descripcion: desc }); }}>
      <div className="campo"><label htmlFor="me-desc">Descripción</label><input id="me-desc" value={desc} onChange={(ev) => setDesc(ev.target.value)} /></div>
      {unidades && <div className="campo"><label htmlFor="me-dec">Decimales</label><input id="me-dec" type="number" min={0} max={6} value={dec} onChange={(ev) => setDec(ev.target.value)} /></div>}
      <div className="fila"><button type="submit" className="principal">Guardar</button><button type="button" onClick={cerrar}>Cancelar</button></div>
    </form>
  );
}
